import React from "react";
import { useTranslation } from "react-i18next";
import { Minus, TrendingDown, TrendingUp } from "lucide-react";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
  Tooltip,
} from "recharts";
import type { RevenueSlice, RevenueTrend } from "../types/analyticsDashboard";
import { formatCurrency, getRevenuePercent } from "../utils/revenueFormatting";

type RevenueDonutTooltipProps = {
  activeSlice: RevenueSlice;
  percent: number;
  periodLabel: string;
};

const RevenueDonutTooltip = ({
  activeSlice,
  percent,
  periodLabel,
}: RevenueDonutTooltipProps): React.JSX.Element => {
  return (
    <div className="min-h-[50px] min-w-[222px] rounded-md border border-[#E4E4E7] bg-white px-2.5 py-1.5 shadow-[0_2px_4px_-2px_rgba(0,0,0,0.1),0_4px_6px_-1px_rgba(0,0,0,0.1)]">
      <p className="text-xs font-medium leading-none text-zinc-800">
        {periodLabel}
      </p>
      <p className="mt-1 flex items-center whitespace-nowrap text-xs leading-4 text-zinc-700">
        <span
          className="mr-2 inline-block size-2 rounded-full"
          style={{ backgroundColor: activeSlice.color }}
        />
        <span className="h-4 text-xs leading-4 font-normal text-[#71717A]">
          {activeSlice.name}
        </span>
        <span className="ml-2 h-4 w-[60px] text-xs leading-4 font-medium text-[#000000]">
          {formatCurrency(activeSlice.value, activeSlice.currency)}
        </span>
        <span className="ml-2 h-4 w-[18px] text-xs leading-4 font-semibold text-[#CAC19F]">
          {percent}%
        </span>
      </p>
    </div>
  );
};

type RevenueDistributionSectionProps = {
  revenueDistribution: RevenueSlice[];
  totalRevenueRounded: number;
  currency: string;
  periodLabel: string;
  periodContextKey: string;
  revenueTrend: RevenueTrend;
};

export const RevenueDistributionSection = ({
  revenueDistribution,
  totalRevenueRounded,
  currency,
  periodLabel,
  periodContextKey,
  revenueTrend,
}: RevenueDistributionSectionProps): React.JSX.Element => {
  const { t } = useTranslation();

  const trendLabel = (() => {
    if (!revenueTrend.hasComparison) {
      return t(
        "analytics.admin.totalRevenue.trendingNoComparison",
        "No comparison data for previous period",
      );
    }

    if (revenueTrend.direction === "up") {
      return t("analytics.admin.totalRevenue.trendingUp", {
        defaultValue: "Trending up by {{percent}}% this period",
        percent: revenueTrend.percent,
      });
    }

    if (revenueTrend.direction === "down") {
      return t("analytics.admin.totalRevenue.trendingDown", {
        defaultValue: "Trending down by {{percent}}% this period",
        percent: revenueTrend.percent,
      });
    }

    return t(
      "analytics.admin.totalRevenue.trendingFlat",
      "No change from previous period",
    );
  })();

  let TrendIcon = Minus;
  if (revenueTrend.direction === "up") {
    TrendIcon = TrendingUp;
  } else if (revenueTrend.direction === "down") {
    TrendIcon = TrendingDown;
  }
  const chartSlices = React.useMemo(
    () => revenueDistribution.filter((slice) => slice.value > 0),
    [revenueDistribution],
  );
  const hasChartData = chartSlices.length > 0 && totalRevenueRounded > 0;

  const [activeIndex, setActiveIndex] = React.useState<number | undefined>(
    undefined,
  );
  const renderActiveShape = (shapeData: unknown): React.JSX.Element => {
    const sectorProps = (shapeData ?? {}) as React.ComponentProps<
      typeof Sector
    > & {
      innerRadius?: number;
      outerRadius?: number;
      fill?: string;
    };
    const currentInnerRadius = Number(sectorProps.innerRadius ?? 90);
    const currentOuterRadius = Number(sectorProps.outerRadius ?? 128);
    const currentFill = sectorProps.fill ?? "#7C6BF2";

    return (
      <>
        <Sector
          {...sectorProps}
          innerRadius={currentOuterRadius + 3}
          outerRadius={currentOuterRadius + 9}
          fill={currentFill}
          opacity={0.95}
          stroke="none"
        />
        <Sector
          {...sectorProps}
          innerRadius={currentInnerRadius}
          outerRadius={currentOuterRadius + 4}
          fill={currentFill}
          stroke="none"
        />
      </>
    );
  };

  return (
    <div className="flex h-[419px] flex-col rounded-[8px] p-0">
      <div className="flex h-[50px] w-full flex-col items-center justify-start gap-1.5 text-center">
        <h2 className="h-6 w-full text-base font-medium leading-normal tracking-normal text-[#0A0A0A]">
          {t("analytics.admin.totalRevenue.title", "Total Revenue")}
        </h2>
        <p className="h-5 w-full text-center text-sm font-normal leading-normal tracking-normal text-[#737373]">
          {periodLabel}
        </p>
      </div>

      <div className="relative mx-auto my-6 h-[280px] w-[320px]">
        {!hasChartData ? (
          <svg
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-1/2 size-[256px] -translate-x-1/2 -translate-y-1/2"
            viewBox="0 0 256 256"
          >
            <circle
              cx="128"
              cy="128"
              r="109"
              fill="none"
              stroke="#ECEAF7"
              strokeWidth="38"
            />
          </svg>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartSlices}
                dataKey="value"
                nameKey="name"
                innerRadius={90}
                outerRadius={128}
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(undefined)}
                paddingAngle={4}
                stroke="#ffffff"
                strokeWidth={4}
              >
                {chartSlices.map((slice) => (
                  <Cell key={slice.hotelId} fill={slice.color} />
                ))}
              </Pie>
              <Tooltip
                cursor={false}
                wrapperStyle={{ zIndex: 30 }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) {
                    return null;
                  }

                  const activeSlice = payload[0].payload as RevenueSlice;
                  const percent = getRevenuePercent(
                    activeSlice.value,
                    totalRevenueRounded,
                  );

                  return (
                    <RevenueDonutTooltip
                      activeSlice={activeSlice}
                      percent={percent}
                      periodLabel={periodLabel}
                    />
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        )}

        <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center">
          <p className="text-4xl font-semibold leading-none text-zinc-900">
            {formatCurrency(totalRevenueRounded, currency)}
          </p>
          <p className="mt-2 text-xs text-zinc-500">
            {t("analytics.admin.totalRevenue.centerLabel", "Total revenue")}
          </p>
        </div>
      </div>

      <div className="space-y-1 text-center">
        <p className="mx-auto inline-flex h-4 w-full items-center justify-center gap-2 whitespace-nowrap text-sm font-medium leading-none tracking-normal text-[#0A0A0A]">
          {trendLabel}
          {revenueTrend.hasComparison && revenueTrend.direction !== "flat" ? (
            <TrendIcon className="size-4 text-[#8B7CF6]" />
          ) : null}
        </p>
        <p className="h-[14px] w-full text-center text-sm font-normal leading-none tracking-normal text-[#737373]">
          {t(periodContextKey)}
        </p>
      </div>
    </div>
  );
};
